import { Helmet } from '@dr.pogodin/react-helmet';
import { CaretLeftIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { addWeeks, endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Api, useApiCallable } from 'src/api';
import {
  Button,
  ConfirmDeleteModal,
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Text,
} from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer, useTimerChange } from 'src/contexts';
import { useMyUserClient } from 'src/hooks';
import { Model } from 'src/models';
import {
  formatAsDate,
  formatAsDayLabel,
  formatAsISO,
  formatTimeSpent,
} from 'src/util';

import { ProfileTabs, TimeLogDayCard, TimeLogEntryModal } from '../components';

/** Weeks run Monday to Sunday, as the working week does. */
const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

type Entry = Model.WorkDone.Logged<Api.WorkDone.V_List>;

export default function TimeLogPage() {
  const myUser = useMyUserClient();
  const callApi = useApiCallable();
  const timer = useTimer();

  const [weeksBack, setWeeksBack] = React.useState(0);
  const [editing, setEditing] = React.useState<{ entry: Entry | null } | null>(
    null
  );
  const [deleting, setDeleting] = React.useState<Entry | null>(null);

  const start = startOfWeek(addWeeks(new Date(), -weeksBack), WEEK_OPTIONS);
  const end = endOfWeek(start, WEEK_OPTIONS);

  const api = Api.WorkDone.useReadCollectionUsersSub(myUser.id, {
    'loggedAt[after]': formatAsISO(start),
    'loggedAt[before]': formatAsISO(end),
  });

  // Stopping a timer banks minutes, which land in this week's list.
  useTimerChange(api.invalidate);

  if (api.error) return <PageError {...api} />;
  const workDones = api.data?.['hydra:member'];
  if (!workDones) return <PageLoading />;

  const days = Model.WorkDone.groupByDay(workDones);
  const totalMinutes = Model.WorkDone.sumMinutes(workDones);
  const isThisWeek = weeksBack === 0;
  const today = formatAsDate(new Date());

  async function remove(entry: Entry) {
    setDeleting(null);
    await callApi(`/work-dones/${entry.id}`, { method: 'DELETE' });
    api.invalidate();
  }

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="small">
      <Helmet>
        <title>Time Log</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1}>
        <Text typeface="headline" as="h1">
          Account
        </Text>

        <ProfileTabs selected="time-log" />

        <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
          <html.div style={[Style.row, Style.alignCenter, styles.period]}>
            <html.button
              style={styles.step}
              aria-label="Previous week"
              onClick={() => setWeeksBack((v) => v + 1)}
            >
              <CaretLeftIcon size={14} color={Color.neutral80} />
            </html.button>
            <Text typeface="body.small.medium" color="neutral100">
              {`${format(start, 'd MMM')} – ${format(end, 'd MMM')}`}
            </Text>
            <html.button
              style={styles.step}
              aria-label="Next week"
              disabled={isThisWeek}
              onClick={() => setWeeksBack((v) => Math.max(v - 1, 0))}
            >
              <CaretRightIcon
                size={14}
                color={isThisWeek ? Color.neutral20 : Color.neutral80}
              />
            </html.button>
            {isThisWeek && (
              <Text typeface="caption.medium" color="neutral40">
                This week
              </Text>
            )}
          </html.div>

          <html.div style={[Style.row, Style.alignCenter, styles.actions]}>
            <Text typeface="body.small.medium" color="neutral60">
              {`${formatTimeSpent(totalMinutes) ?? '0m'} logged`}
            </Text>
            <Button
              label="Log time"
              iconLeft={(p) => <PlusIcon {...p} />}
              onClick={() => setEditing({ entry: null })}
            />
          </html.div>
        </html.div>

        {days.length === 0 && !(isThisWeek && timer.running) ? (
          <EmptyState
            title="Nothing logged this week"
            subtitle="Start a timer from a ticket, project or client and it lands here. You can also log time by hand."
          />
        ) : (
          days.map((day) => (
            <TimeLogDayCard
              key={day.date}
              label={formatAsDayLabel(day.date)}
              day={day}
              showRunning={isThisWeek && day.date === today}
              onEdit={(entry) => setEditing({ entry })}
              onDelete={setDeleting}
            />
          ))
        )}

        {/* A timer started before anything was banked today has no day card to
            sit on, so today's gets drawn for it. */}
        {isThisWeek &&
          timer.running &&
          !days.some((day) => day.date === today) && (
            <TimeLogDayCard
              label={formatAsDayLabel(today)}
              day={{ date: today, workDones: [], minutes: 0 }}
              showRunning
              onEdit={(entry) => setEditing({ entry })}
              onDelete={setDeleting}
            />
          )}
      </Stack>

      {editing && (
        <TimeLogEntryModal
          existing={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            api.invalidate();
          }}
        />
      )}

      {deleting && (
        <ConfirmDeleteModal
          title="Delete this entry?"
          message={`${
            formatTimeSpent(deleting.minutes) ?? 'This entry'
          } will be removed from ${format(
            parseISO(deleting.loggedAt),
            'EEE d MMM'
          )}. This cannot be undone.`}
          onConfirm={() => remove(deleting)}
          onClose={() => setDeleting(null)}
        />
      )}
    </PageWrap>
  );
}

const styles = css.create({
  period: {
    gap: 8,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 6,
    backgroundColor: color.neutral0,
    cursor: 'pointer',
  },
  actions: {
    gap: 16,
  },
});
